// src/Sidebar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaAngleRight } from 'react-icons/fa6';
import { FaRegIdCard } from "react-icons/fa";
import { TbBell, TbChartHistogram, TbCheck, TbClock, TbFilePencil, TbHomePlus, TbMap2, TbMapPinExclamation, TbTent, TbUserQuestion, TbUsers, TbUser, TbSettings, TbLogout, TbShield, TbShieldCheck, TbExclamationCircle, TbLayoutSidebarLeftCollapse } from "react-icons/tb";
import { LuMegaphone } from "react-icons/lu";
import { useAuth } from './AuthContext';

const sidebarItems = [
  { label: 'Dashboard', icon: <TbChartHistogram />, path: '/dashboard' },
  {
    label: 'Maps',
    icon: <TbMap2 />, 
    path: '/maps',
    children: [
      { label: 'Incident Map', icon: <TbMapPinExclamation />, path: '/maps/incident' },
      { label: 'Evacuation Map', icon: <TbHomePlus />, path: '/maps/evacuation' },
    ]
  },
  { label: 'Reports', icon: <TbFilePencil />, path: '/incident-reports' },
  { label: 'Announcements', icon: <LuMegaphone />, path: '/announcements' },
  {
    label: 'Missing Person/Pets',
    icon: <TbUserQuestion />,
    path: '/missing-reports',
    children: [
      { label: 'Missing Reports', icon: <TbUserQuestion />, path: '/missing-reports' },
      { label: 'Found Reports', icon: <TbUsers />, path: '/missing-report-interactions' },
    ]
  },
  { label: 'Evacuation Shelters', icon: <TbHomePlus />, path: '/evacuation-shelters' },
  {
    label: 'Temporary Shelters',
    icon: <TbTent />,
    path: '/temporary-shelters',
    children: [
      { label: 'Approved Shelters', icon: <TbCheck />, path: '/temporary-shelters/approved' },
      { label: 'Pending Shelters', icon: <TbClock />, path: '/temporary-shelters/pending' },
    ]
  },
  { label: 'SOS Beacons', icon: <TbBell />, path: '/sos-beacons' },
  {
    label: 'Responder Management',
    icon: <TbShield />,
    path: '/responders',
    children: [
      { label: 'Applications', icon: <TbClock />, path: '/responder-applications' },
      { label: 'Approved Responders', icon: <TbShieldCheck />, path: '/approved-responders' },
      { label: 'Incident Tracking', icon: <TbChartHistogram />, path: '/responder-incident-tracking' },
      { label: 'Responder Map', icon: <TbMapPinExclamation />, path: '/responder-map' },
    ]
  },
  { label: 'Manage Users', icon: <TbUsers />, path: '/manage-users' },
  { label: 'ID Verification', icon: <FaRegIdCard />, path: '/id-verification' },
];

export default function Sidebar({ onNarrowChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  // Initialize submenu states from localStorage
  const [openMaps, setOpenMaps] = useState(() => {
    const savedState = localStorage.getItem('sidebarOpenMaps');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [openShelters, setOpenShelters] = useState(() => {
    const savedState = localStorage.getItem('sidebarOpenShelters');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [openMissing, setOpenMissing] = useState(() => {
    const savedState = localStorage.getItem('sidebarOpenMissing');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [openResponders, setOpenResponders] = useState(() => {
    const savedState = localStorage.getItem('sidebarOpenResponders');
    return savedState ? JSON.parse(savedState) : false;
  });
  // Initialize collapsed state from localStorage or default based on screen size
  const [collapsed, setCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      return JSON.parse(savedState);
    }
    // Default to collapsed on smaller screens (≤925px)
    return window.innerWidth <= 925;
  });
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Notify parent component about initial collapsed state on mount
  useEffect(() => {
    if (onNarrowChange) {
      onNarrowChange(collapsed);
    }
  }, [collapsed, onNarrowChange]); // Include both dependencies

  // Handle window resize to automatically collapse/expand sidebar
  useEffect(() => {
    const handleResize = () => {
      const shouldBeCollapsed = window.innerWidth <= 925;
      if (collapsed !== shouldBeCollapsed) {
        setCollapsed(shouldBeCollapsed);
        localStorage.setItem('sidebarCollapsed', JSON.stringify(shouldBeCollapsed));
        if (onNarrowChange) {
          onNarrowChange(shouldBeCollapsed);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collapsed, onNarrowChange]);

  const toggleSidebar = () => {
    setIsTransitioning(true);
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    
    // Save state to localStorage
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
    
    onNarrowChange && onNarrowChange(newCollapsedState);
    
    // Reset transition state after animation completes
    setTimeout(() => setIsTransitioning(false), 400);
  };

  const handleItemClick = (item) => {
    if (item.label === 'Maps') {
      const newState = !openMaps;
      setOpenMaps(newState);
      localStorage.setItem('sidebarOpenMaps', JSON.stringify(newState));
    } else if (item.label === 'Temporary Shelters') {
      const newState = !openShelters;
      setOpenShelters(newState);
      localStorage.setItem('sidebarOpenShelters', JSON.stringify(newState));
    } else if (item.label === 'Missing Person/Pets') {
      const newState = !openMissing;
      setOpenMissing(newState);
      localStorage.setItem('sidebarOpenMissing', JSON.stringify(newState));
    } else if (item.label === 'Responder Management') {
      const newState = !openResponders;
      setOpenResponders(newState);
      localStorage.setItem('sidebarOpenResponders', JSON.stringify(newState));
    } else {
      navigate(item.path);
    }
  };

  return (
    <div
      className="sidebar-component"
      style={{
        width: collapsed ? 72 : 280,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fdfe 100%)',
        padding: collapsed ? 12 : 24,
        minHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'visible',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        boxShadow: collapsed 
          ? '1px 0 4px rgba(8, 177, 199, 0.1)' 
          : '4px 0 20px rgba(8, 177, 199, 0.15)',
        zIndex: 1000,
        borderRight: collapsed ? 'none' : '1px solid rgba(8, 177, 199, 0.1)',
        transform: isTransitioning ? 'translateZ(0)' : 'none', // Hardware acceleration
        display: 'flex',
        flexDirection: 'column'
      }}
    >
             <style>
         {`
           /* Enhanced sidebar animations */
           .sidebar-link:not(.active):hover {
             background: linear-gradient(135deg, #e8f4f8 0%, #f0f9fc 100%) !important;
             color: #08b1c7 !important;
             transform: translateX(6px) scale(1.02);
             box-shadow: 0 4px 12px rgba(8, 177, 199, 0.2);
           }
           .sidebar-link:not(.active):hover span {
             color: #08b1c7 !important;
           }
           /* Special hover effect for logout button */
           .sidebar-link[style*="color: rgb(220, 38, 38)"]:hover {
             background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%) !important;
             color: #dc2626 !important;
             transform: translateX(6px) scale(1.02);
             box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
           }
           .sidebar-link[style*="color: rgb(220, 38, 38)"]:hover span {
             color: #dc2626 !important;
           }
           .sidebar-child-link:not(.active):hover {
             background: linear-gradient(135deg, #f0f9fc 0%, #f8fdfe 100%) !important;
             color: #08b1c7 !important;
             transform: translateX(4px) scale(1.01);
             box-shadow: 0 2px 8px rgba(8, 177, 199, 0.15);
           }
           .sidebar-link, .sidebar-child-link {
             transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
           }
           .sidebar-link.active {
             background: linear-gradient(135deg, #08b1c7 0%, #0a7a89 100%) !important;
             box-shadow: 0 4px 16px rgba(8, 177, 199, 0.3) !important;
           }
           .sidebar-child-link.active {
             background: linear-gradient(135deg, #08b1c7 0%, #0a7a89 100%) !important;
             box-shadow: 0 2px 12px rgba(8, 177, 199, 0.25) !important;
           }
           .toggle-btn {
             transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
           }
           .sidebar-toggle:hover {
             background: rgba(8, 177, 199, 0.1) !important;
             transform: scale(1.1) !important;
           }
           .sidebar-toggle:active {
             transform: scale(0.95) !important;
           }
           .sidebar-toggle-icon {
             display: inline-flex;
             transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
           }
           /* Focus styles for accessibility */
           .sidebar-link:focus {
             outline: 2px solid #08b1c7;
             outline-offset: 2px;
           }
           .toggle-btn:focus {
             outline: 2px solid #08b1c7;
             outline-offset: 2px;
           }
           /* Text fade animations */
           .sidebar-text {
             opacity: ${collapsed ? '0' : '1'};
             transition: opacity 0.3s ease ${collapsed ? '0s' : '0.2s'};
           }
         `}
       </style>

      

      {/* Sidebar Header */}
       <div style={{
         marginBottom: collapsed ? 24 : 32,
         marginTop: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        padding: '8px 16px',
        width: '100%',
        boxSizing: 'border-box',
        gap: 12
      }}>
        {/* Logo text on the left when expanded */}
        <div style={{
          fontSize: 18,
          fontWeight: '600',
          color: '#08b1c7',
          display: collapsed ? 'none' : 'block',
          padding: '8px 12px'
        }}>
          GabaySakuna
        </div>
        {/* Toggle on the right when expanded; centered when collapsed */}
        <button
          onClick={toggleSidebar}
          type="button"
          style={{
            padding: '12px',
            cursor: 'pointer',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#08b1c7',
            fontSize: 18,
            transition: 'all 0.2s ease',
            background: 'transparent',
            marginLeft: collapsed ? 0 : 'auto',
            border: 'none'
          }}
          className="sidebar-toggle"
          title={collapsed ? 'Expand Menu' : 'Collapse Menu'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span
            className="sidebar-toggle-icon"
            style={{
              transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            <TbLayoutSidebarLeftCollapse size={22} />
          </span>
        </button>
      </div>

      {/* Navigation Items */}
      <div style={{ paddingLeft: collapsed ? 0 : 8, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {sidebarItems.map(item => {
          const isActive =
            location.pathname === item.path ||
            (item.children &&
              ((item.label === 'Maps' && openMaps) ||
                (item.label === 'Temporary Shelters' && openShelters) ||
                (item.label === 'Missing Person/Pets' && openMissing) ||
                (item.label === 'Responder Management' && openResponders)) &&
              location.pathname.startsWith(item.path));
          
          return (
            <React.Fragment key={item.label}>
              <div
                onClick={() => handleItemClick(item)}
                className={`sidebar-link${isActive ? ' active' : ''}`}
                style={{
                  marginBottom: collapsed ? 8 : 12,
                  color: isActive ? '#fff' : '#5faab7',
                  background: isActive ? '#08b1c7' : 'transparent',
                  borderRadius: 12,
                  padding: collapsed ? '12px 8px' : '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: collapsed ? 0 : 12,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  fontWeight: isActive ? '600' : '500'
                }}
                title={collapsed ? item.label : undefined}
              >
                <span style={{ 
                  fontSize: collapsed ? 18 : 20, 
                  color: isActive ? '#fff' : '#08b1c7',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {item.icon}
                </span>
                                 {!collapsed && (
                   <span className="sidebar-text" style={{ 
                     fontSize: 14,
                     color: isActive ? '#fff' : '#4b5563',
                     fontWeight: isActive ? '600' : '500'
                   }}>
                     {item.label}
                   </span>
                 )}
                {item.children && !collapsed && (
                  <span className="sidebar-text" style={{
                    color: isActive ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
                    marginLeft: 'auto',
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: (
                      (item.label === 'Maps' && openMaps) ||
                      (item.label === 'Temporary Shelters' && openShelters) ||
                      (item.label === 'Missing Person/Pets' && openMissing) ||
                      (item.label === 'Responder Management' && openResponders)
                    ) ? 'rotate(90deg)' : 'rotate(0deg)',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <FaAngleRight />
                  </span>
                )}
              </div>

              {/* Child Items */}
              {item.children && ((item.label === 'Maps' && openMaps) || (item.label === 'Temporary Shelters' && openShelters) || (item.label === 'Missing Person/Pets' && openMissing) || (item.label === 'Responder Management' && openResponders)) && !collapsed && (
                <div style={{ 
                  marginLeft: 24,
                  marginBottom: 8,
                  borderLeft: '2px solid #e8f4f8',
                  paddingLeft: 16
                }}>
                  {item.children.map(child => {
                    const isChildActive = location.pathname === child.path;
                    return (
                      <div
                        key={child.label}
                        onClick={() => navigate(child.path)}
                        className={`sidebar-child-link${isChildActive ? ' active' : ''}`}
                        style={{
                          marginBottom: 8,
                          color: isChildActive ? '#fff' : '#5faab7',
                          background: isChildActive ? '#08b1c7' : 'transparent',
                          fontWeight: 'normal',
                          borderRadius: 10,
                          padding: '10px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          transition: 'all 0.2s ease',
                          fontSize: 13
                        }}
                        title={child.label}
                      >
                        <span style={{ 
                          fontSize: 16, 
                          color: isChildActive ? '#fff' : '#08b1c7',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          {child.icon}
                        </span>
                                                 <span className="sidebar-text" style={{ 
                           color: isChildActive ? '#fff' : '#6b7280',
                           fontSize: 13,
                           fontWeight: isChildActive ? '600' : '500'
                         }}>
                           {child.label}
                         </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Bottom Section - Profile Settings and Logout */}
      <div style={{ 
        paddingLeft: collapsed ? 0 : 8,
        borderTop: '1px solid rgba(8, 177, 199, 0.15)',
        paddingTop: 16,
        marginTop: 16
      }}>
        {/* Profile Settings */}
        <div
          onClick={() => navigate('/profile')}
          className="sidebar-link"
          style={{
            marginBottom: 8,
            color: location.pathname === '/profile' ? '#fff' : '#5faab7',
            background: location.pathname === '/profile' ? '#08b1c7' : 'transparent',
            borderRadius: 12,
            padding: collapsed ? '12px 8px' : '12px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : 12,
            justifyContent: collapsed ? 'center' : 'flex-start',
            transition: 'all 0.2s ease',
            position: 'relative',
            fontWeight: location.pathname === '/profile' ? '600' : '500'
          }}
          title={collapsed ? 'Profile Settings' : undefined}
        >
          <span style={{ 
            fontSize: collapsed ? 18 : 20, 
            color: location.pathname === '/profile' ? '#fff' : '#08b1c7',
            display: 'flex',
            alignItems: 'center'
          }}>
            <TbUser />
          </span>
          {!collapsed && (
            <span className="sidebar-text" style={{ 
              fontSize: 14,
              color: location.pathname === '/profile' ? '#fff' : '#4b5563',
              fontWeight: location.pathname === '/profile' ? '600' : '500'
            }}>
              Profile Settings
            </span>
          )}
        </div>

        {/* General Settings */}
        <div
          onClick={() => navigate('/settings')}
          className="sidebar-link"
          style={{
            marginBottom: 8,
            color: location.pathname === '/settings' ? '#fff' : '#5faab7',
            background: location.pathname === '/settings' ? '#08b1c7' : 'transparent',
            borderRadius: 12,
            padding: collapsed ? '12px 8px' : '12px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : 12,
            justifyContent: collapsed ? 'center' : 'flex-start',
            transition: 'all 0.2s ease',
            position: 'relative',
            fontWeight: location.pathname === '/settings' ? '600' : '500'
          }}
          title={collapsed ? 'Settings' : undefined}
        >
          <span style={{ 
            fontSize: collapsed ? 18 : 20, 
            color: location.pathname === '/settings' ? '#fff' : '#08b1c7',
            display: 'flex',
            alignItems: 'center'
          }}>
            <TbSettings />
          </span>
          {!collapsed && (
            <span className="sidebar-text" style={{ 
              fontSize: 14,
              color: location.pathname === '/settings' ? '#fff' : '#4b5563',
              fontWeight: location.pathname === '/settings' ? '600' : '500'
            }}>
              Settings
            </span>
          )}
        </div>

        {/* Logout */}
        <div
          onClick={async () => {
            try {
              await signOut();
              navigate('/login');
            } catch (error) {
              console.error('Error signing out:', error);
            }
          }}
          className="sidebar-link"
          style={{
            marginBottom: 8,
            color: '#dc2626',
            background: 'transparent',
            borderRadius: 12,
            padding: collapsed ? '12px 8px' : '12px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : 12,
            justifyContent: collapsed ? 'center' : 'flex-start',
            transition: 'all 0.2s ease',
            position: 'relative',
            fontWeight: '500'
          }}
          title={collapsed ? 'Logout' : undefined}
        >
          <span style={{ 
            fontSize: collapsed ? 18 : 20, 
            color: '#dc2626',
            display: 'flex',
            alignItems: 'center'
          }}>
            <TbLogout />
          </span>
          {!collapsed && (
            <span className="sidebar-text" style={{ 
              fontSize: 14,
              color: '#dc2626',
              fontWeight: '500'
            }}>
              Logout
            </span>
          )}
        </div>
      </div>

      {/* Bottom Spacing */}
      <div style={{ height: 24 }}></div>
    </div>
  );
}